export interface LoginCredentials {
  email: string;
  password: string;
  remember?: boolean;
  language?: string;
  captchaToken?: string;
}

export interface RegisterData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  password: string;
  password_confirmation: string;
  newsletter_subscribed?: boolean;
  agreements?: Array<{ id: number; accepted: boolean }>;
  captchaToken?: string;
  language?: string;
}

export interface ForgotPasswordData {
  email: string;
  language?: string;
}

export interface AuthResponse {
  success: boolean;
  data?: {
    customer?: unknown;
    message?: string;
  };
  message?: string;
  error?: string;
  error_key?: string;
  errors?: Record<string, string[]>;
}

class ApiRequestBuilder {
  static buildLoginBody(credentials: LoginCredentials): Record<string, unknown> {
    const body: Record<string, unknown> = {
      email_or_phone: credentials.email,
      password: credentials.password,
      remember: credentials.remember || false,
    };

    if (credentials.language) {
      body.language = credentials.language;
    }

    if (credentials.captchaToken) {
      body.captcha_token = credentials.captchaToken;
    }

    return body;
  }

  static buildRegisterBody(data: RegisterData): Record<string, unknown> {
    const body: Record<string, unknown> = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      password: data.password,
      password_confirmation: data.password_confirmation,
      newsletter_subscribed: data.newsletter_subscribed || false,
    };

    if (data.phone) {
      body.phone = data.phone;
    }

    if (data.agreements) {
      body.agreements = data.agreements;
    }

    if (data.captchaToken) {
      body.captcha_token = data.captchaToken;
    }

    if (data.language) {
      body.language = data.language;
    }

    return body;
  }

  static buildForgotPasswordBody(data: ForgotPasswordData): Record<string, unknown> {
    const body: Record<string, unknown> = {
      email: data.email,
    };

    if (data.language) {
      body.language = data.language;
    }

    return body;
  }
}

class ApiResponseHandler {
  static async handleResponse<T>(response: Response): Promise<AuthResponse> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || errorData.message || 'Request failed',
        error_key: errorData.error_key,
        errors: errorData.errors,
        message: errorData.message,
      };
    }

    const data = await response.json();

    if (!data.success) {
      return {
        success: false,
        error: data.error || data.message || 'Request failed',
        error_key: data.error_key,
        errors: data.errors,
        message: data.message,
      };
    }

    return {
      success: true,
      data: data.data,
      message: data.message,
    };
  }

  static handleError(error: unknown, defaultMessage: string): AuthResponse {
    return {
      success: false,
      error: error instanceof Error ? error.message : defaultMessage,
    };
  }
}

export class AuthenticationService {
  private static readonly LOGIN_ENDPOINT = '/api/customer/auth/login';
  private static readonly REGISTER_ENDPOINT = '/api/customer/auth/register';
  private static readonly FORGOT_PASSWORD_ENDPOINT = '/api/customer/auth/forgot-password';
  private static readonly RESET_PASSWORD_ENDPOINT = '/api/customer/auth/reset-password';
  private static readonly LOGOUT_ENDPOINT = '/api/customer/auth/logout';
  private static readonly ME_ENDPOINT = '/api/customer/me';
  private static readonly VERIFY_EMAIL_ENDPOINT = '/api/customer/auth/verify-email';
  private static readonly RESEND_VERIFICATION_ENDPOINT = '/api/customer/auth/resend-verification';

  static async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const requestBody = ApiRequestBuilder.buildLoginBody(credentials);

      const response = await fetch(this.LOGIN_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      return await ApiResponseHandler.handleResponse(response);
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Login failed');
    }
  }

  static async register(data: RegisterData): Promise<AuthResponse> {
    try {
      const requestBody = ApiRequestBuilder.buildRegisterBody(data);

      const response = await fetch(this.REGISTER_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      return await ApiResponseHandler.handleResponse(response);
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Registration failed');
    }
  }

  static async forgotPassword(data: ForgotPasswordData): Promise<AuthResponse> {
    try {
      const requestBody = ApiRequestBuilder.buildForgotPasswordBody(data);

      const response = await fetch(this.FORGOT_PASSWORD_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      return await ApiResponseHandler.handleResponse(response);
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Failed to send password reset email');
    }
  }

  static async logout(): Promise<AuthResponse> {
    try {
      const response = await fetch(this.LOGOUT_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
      });

      return await ApiResponseHandler.handleResponse(response);
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Logout failed');
    }
  }

  static async getMe(forceRefresh = false): Promise<AuthResponse> {
    try {
      const url = `${this.ME_ENDPOINT}${forceRefresh ? `?_t=${Date.now()}` : ''}`;
      
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          return {
            success: false,
            error: 'Unauthorized',
            message: 'Giriş yapmanız gerekiyor',
          };
        }

        return await ApiResponseHandler.handleResponse(response);
      }

      const data = await response.json();

      if (data.success && data.data?.customer) {
        return {
          success: true,
          data: data.data,
          message: data.message,
        };
      }

      return {
        success: false,
        error: 'Customer data not found',
      };
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Failed to fetch customer');
    }
  }

  static async resetPassword(data: {
    token: string;
    email: string;
    password: string;
    password_confirmation: string;
    language?: string;
  }): Promise<AuthResponse> {
    try {
      const requestBody: Record<string, unknown> = {
        token: data.token,
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
      };

      if (data.language) {
        requestBody.language = data.language;
      }

      const response = await fetch(this.RESET_PASSWORD_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      return await ApiResponseHandler.handleResponse(response);
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Password reset failed');
    }
  }

  static async verifyEmail(token: string): Promise<AuthResponse> {
    try {
      const response = await fetch(`${this.VERIFY_EMAIL_ENDPOINT}/${encodeURIComponent(token)}`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        credentials: 'include',
      });

      return await ApiResponseHandler.handleResponse(response);
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Email verification failed');
    }
  }

  static async resendVerification(data: {
    email: string;
    language?: string;
  }): Promise<AuthResponse> {
    try {
      const requestBody: Record<string, unknown> = {
        email: data.email,
      };

      if (data.language) {
        requestBody.language = data.language;
      }

      const response = await fetch(this.RESEND_VERIFICATION_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      return await ApiResponseHandler.handleResponse(response);
    } catch (error) {
      return ApiResponseHandler.handleError(error, 'Failed to resend verification email');
    }
  }
}
